import React from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { BiomarkerItem } from "../../../../common/entities/type";
import {
  getBiomarkerPointerColor,
  getChangeInfo,
  getEntryAtDate,
} from "./biomarkerDisplay";

/**
 * Tiny trend sparkline + change badge shown next to the value on the All
 * biomarkers cards: the previous and the current reading, each dot colored by
 * the category that reading scored, followed by "↑5%" / "↓8%".
 *
 * Everything comes from the backend's `item.change` (the previous value and
 * date it compared against, and the percentage) — the frontend never derives a
 * change. No `change`, no trend: line and percentage are both hidden.
 */
type Props = {
  item: BiomarkerItem;
};

const SPARK_WIDTH = 44;
const SPARK_HEIGHT = 18;

const TrendSparkline = ({ item }: Props) => {
  const theme: Theme = useTheme();

  const change = getChangeInfo(item);
  if (item.qualitative || !change) return null;
  if (typeof item.value !== "number" || isNaN(item.value)) return null;

  const previousEntry = getEntryAtDate(item, change.previousDate);
  // Oldest → newest: the reading compared against, then the current one.
  const entries = [
    {
      value: change.previousValue,
      color: getBiomarkerPointerColor(previousEntry?.scoreCategory ?? 0, theme),
    },
    {
      value: item.value,
      color: getBiomarkerPointerColor(item.scoreCategory, theme),
    },
  ];

  const values = entries.map((e) => e.value);
  const minV = Math.min(...values);
  const maxV = Math.max(...values);
  const span = maxV - minV || 1;
  const pad = 3;

  const points = values.map((v, i) => {
    const x =
      values.length === 1
        ? SPARK_WIDTH - pad
        : pad + (i / (values.length - 1)) * (SPARK_WIDTH - pad * 2);
    const y = SPARK_HEIGHT - pad - ((v - minV) / span) * (SPARK_HEIGHT - pad * 2);
    return { x, y };
  });

  const arrow =
    change.direction === "down" ? "↓" : change.direction === "flat" ? "→" : "↑";

  return (
    <div style={styles.wrapper}>
      <svg width={SPARK_WIDTH} height={SPARK_HEIGHT}>
        {points.length > 1 && (
          <polyline
            points={points.map((p) => `${p.x},${p.y}`).join(" ")}
            fill="none"
            stroke={theme.colors_ds.NEUTRAL_BORDER_DEFAULT}
            strokeWidth={1.5}
          />
        )}
        {points.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={2.5}
            fill={entries[i].color}
          />
        ))}
      </svg>
      <span style={styles.changeText(theme)}>
        {arrow}
        {change.pct}%
      </span>
    </div>
  );
};

export default TrendSparkline;

const styles = {
  wrapper: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    // Keeps its size next to a wrapping value.
    flexShrink: 0,
    height: "20px",
  },
  changeText: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "11px",
    lineHeight: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    whiteSpace: "nowrap" as const,
  }),
};
